import { useCallback, useEffect, useState } from 'react';

import Swal from 'sweetalert2';

import { Box, Container, Divider, Typography } from '@mui/material';

import {
    getAdminProductReviews,
    getAdminReviewDashboard,
    moderateAdminReview,
    saveAdminReviewReply,
    withdrawAdminReviewReply
} from '../../../actions/review';
import { msg } from '../../../actions/swal_msg';
import ModerationDialog from './ModerationDialog';
import ReplyDialog from './ReplyDialog';
import ReviewDashboard from './ReviewDashboard';
import ReviewDetailDialog from './ReviewDetailDialog';
import ReviewTable from './ReviewTable';

const initialFilters = {
    status: 'published',
    search: '',
    rating: '',
    verified: '',
    has_images: '',
    has_reply: '',
    sort: 'newest',
    range: 'all',
    page: 1,
    limit: 25
};

const AdminProductReviews = () => {
    const [dashboardRange, setDashboardRange] = useState('90d');
    const [dashboard, setDashboard] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [dashboardError, setDashboardError] = useState('');
    const [filters, setFilters] = useState(initialFilters);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [reviews, setReviews] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 25,
        total: 0,
        pages: 0
    });
    const [reviewsLoading, setReviewsLoading] = useState(true);
    const [reviewsError, setReviewsError] = useState('');
    const [busyId, setBusyId] = useState('');
    const [detailReview, setDetailReview] = useState(null);
    const [moderationReview, setModerationReview] = useState(null);
    const [replyReview, setReplyReview] = useState(null);
    const {
        has_images: hasImages,
        has_reply: hasReply,
        limit,
        page,
        range,
        rating,
        sort,
        status,
        verified
    } = filters;

    const loadDashboard = useCallback(async () => {
        setDashboardLoading(true);
        setDashboardError('');

        try {
            const body = await getAdminReviewDashboard(dashboardRange);

            if (!body.status) {
                setDashboardError('No se pudo cargar el dashboard.');
                return;
            }

            setDashboard(body.resp);
        } catch (error) {
            setDashboardError('No se pudo cargar el dashboard.');
        } finally {
            setDashboardLoading(false);
        }
    }, [dashboardRange]);

    const loadReviews = useCallback(async () => {
        setReviewsLoading(true);
        setReviewsError('');

        try {
            const body = await getAdminProductReviews({
                has_images: hasImages,
                has_reply: hasReply,
                limit,
                page,
                range,
                rating,
                search: debouncedSearch,
                sort,
                status,
                verified
            });

            if (!body.status) {
                setReviewsError('No se pudieron cargar las reseñas.');
                return;
            }

            setReviews(body.resp.reviews || []);
            setPagination(body.resp.pagination || {
                page: 1,
                limit,
                total: 0,
                pages: 0
            });
        } catch (error) {
            setReviewsError('No se pudieron cargar las reseñas.');
        } finally {
            setReviewsLoading(false);
        }
    }, [
        debouncedSearch,
        hasImages,
        hasReply,
        limit,
        page,
        range,
        rating,
        sort,
        status,
        verified
    ]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 350);

        return () => clearTimeout(timeout);
    }, [filters.search]);

    useEffect(() => {
        loadReviews();
    }, [loadReviews]);

    const refreshModule = async () => {
        await Promise.all([loadDashboard(), loadReviews()]);
    };

    const handleFilterChange = (name, value) => {
        setFilters(current => ({
            ...current,
            [name]: value,
            page: name === 'page' ? value : 1
        }));
    };

    const handleModeration = async model => {
        if (!moderationReview)
            return;

        setBusyId(moderationReview.id);

        try {
            const body = await moderateAdminReview(
                moderationReview.id,
                model
            );

            if (!body.status) {
                msg('warning', 'Moderación',
                    'No se pudo cambiar la visibilidad de la reseña.');
                return;
            }

            setModerationReview(null);
            msg('success', 'Moderación', model.action === 'hide' ?
                'La reseña se ocultó de la tienda.'
            :
                'La reseña volvió a publicarse.');
            await refreshModule();
        } catch (error) {
            msg('error', 'Moderación',
                'No se pudo cambiar la visibilidad de la reseña.');
        } finally {
            setBusyId('');
        }
    };

    const handleReply = async text => {
        if (!replyReview)
            return;

        setBusyId(replyReview.id);

        try {
            const body = await saveAdminReviewReply(replyReview.id, text);

            if (!body.status) {
                msg('warning', 'Respuesta',
                    'No se pudo publicar la respuesta.');
                return;
            }

            setReplyReview(null);
            msg('success', 'Respuesta',
                'La respuesta ya está visible en la tienda.');
            await loadReviews();
        } catch (error) {
            msg('error', 'Respuesta',
                'No se pudo publicar la respuesta.');
        } finally {
            setBusyId('');
        }
    };

    const handleWithdraw = async review => {
        const confirmation = await Swal.fire({
            title: '¿Retirar la respuesta?',
            text: 'Dejará de mostrarse en la tienda, pero se conservará para auditoría.',
            confirmButtonText: 'Retirar',
            cancelButtonText: 'Cancelar',
            showCancelButton: true
        });

        if (!confirmation.isConfirmed)
            return;

        setBusyId(review.id);

        try {
            const body = await withdrawAdminReviewReply(review.id);

            if (!body.status) {
                msg('warning', 'Respuesta',
                    'No se pudo retirar la respuesta.');
                return;
            }

            msg('success', 'Respuesta',
                'La respuesta dejó de mostrarse en la tienda.');
            await loadReviews();
        } catch (error) {
            msg('error', 'Respuesta',
                'No se pudo retirar la respuesta.');
        } finally {
            setBusyId('');
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mb: 5, mt: '90px' }}>
            <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 700 }} variant="h4">
                    Reseñas de productos
                </Typography>
                <Typography color="text.secondary">
                    Consulta el desempeño, responde a clientes y modera el contenido publicado.
                </Typography>
            </Box>

            <ReviewDashboard
                data={dashboard}
                error={dashboardError}
                loading={dashboardLoading}
                onRangeChange={setDashboardRange}
                range={dashboardRange}
            />

            <Divider sx={{ my: 5 }} />

            <ReviewTable
                busyId={busyId}
                error={reviewsError}
                filters={filters}
                loading={reviewsLoading}
                onDetail={setDetailReview}
                onFilterChange={handleFilterChange}
                onModerate={setModerationReview}
                onPageChange={value => handleFilterChange('page', value)}
                onReply={setReplyReview}
                onRowsPerPageChange={value => handleFilterChange('limit', value)}
                onWithdraw={handleWithdraw}
                pagination={pagination}
                reviews={reviews}
            />

            <ReviewDetailDialog
                onClose={() => setDetailReview(null)}
                open={Boolean(detailReview)}
                review={detailReview}
            />
            <ModerationDialog
                busy={Boolean(busyId)}
                onClose={() => setModerationReview(null)}
                onSave={handleModeration}
                open={Boolean(moderationReview)}
                review={moderationReview}
            />
            <ReplyDialog
                busy={Boolean(busyId)}
                onClose={() => setReplyReview(null)}
                onSave={handleReply}
                open={Boolean(replyReview)}
                review={replyReview}
            />
        </Container>
    );
};

export default AdminProductReviews;
